/* eslint-disable max-statements */

import { Button, Flex, Stack, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';

import { EnvironmentCountries, TransitGroup } from '@e-origin/shared';

import { useDeclarants } from '@admin/hooks';
import { Select, TextInput } from '@components';
import { ContentLayout } from '@features/layout';
import { useSettings } from '@hooks/useSettings';
import { NotificationsList } from './NotificationsList';

const allSystems = [
  { label: 'IDMS', value: 'idms', hasLRNSearch: true, country: EnvironmentCountries.BE },
  { label: 'NCTS', value: 'ncts', hasLRNSearch: true, country: EnvironmentCountries.BE },
  { label: 'DMS', value: 'dms', hasLRNSearch: true, country: EnvironmentCountries.NL },
  { label: 'AES', value: 'aes', hasLRNSearch: true, country: EnvironmentCountries.BE },
  { label: 'PNTS', value: 'pnts', hasLRNSearch: true, country: EnvironmentCountries.BE },
];

const transitGroupOptions = [
  { value: TransitGroup.DEPARTURE, label: 'Departure' },
  { value: TransitGroup.ARRIVAL, label: 'Arrival' },
];

export const CustomsSystems = () => {
  const { data: settings } = useSettings();

  const systems = useMemo(() => {
    return allSystems.filter((system) => system.country === settings.country);
  }, [settings?.country]);

  const [selectedSystem, setSelectedSystem] = useState(systems[0]);

  useEffect(() => {
    setSelectedSystem(systems[0]);
  }, [systems]);

  const changesSelectedSystem = (value) => {
    if (!value) return;

    resetNotificationSearch();
    setSelectedSystem(systems.find((system) => system.value === value));
  };

  const { data: declarantsList } = useDeclarants();
  const declarantsOptions = useMemo(
    () => (declarantsList || []).map((declarant) => ({ label: declarant.accountName || declarant.name || 'N/A', value: declarant._id })),
    [declarantsList],
  );
  const [selectedDeclarant, setSelectedDeclarant] = useState(declarantsOptions[0]);

  const changesSelectedDeclarant = (value) => {
    if (!value) return;

    resetNotificationSearch();
    setSelectedDeclarant(declarantsOptions.find((declarant) => declarant.value === value));
  };

  useEffect(() => {
    setSelectedDeclarant(declarantsOptions[0]);
  }, [declarantsOptions]);

  const [selectedLRN, setSelectedLRN] = useState('');
  const [selectedMRN, setSelectedMRN] = useState('');
  const [selectedTransitGroup, setSelectedTransitGroup] = useState('');
  const [selectedCorrelationID, setSelectedCorrelationID] = useState('');
  const [showNotificationsList, setShowNotificationsList] = useState(false);

  const handleSearch = () => {
    setShowNotificationsList(true);
  };

  const resetNotificationSearch = () => {
    setShowNotificationsList(false);
    setSelectedLRN('');
    setSelectedMRN('');
    setSelectedTransitGroup('');
    setSelectedCorrelationID('');
  };

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Customs systems</ContentLayout.Title>
        <Select label="Select system" data={systems} value={selectedSystem?.value} onChange={changesSelectedSystem} />
      </ContentLayout.Header>

      <ContentLayout.Content>
        <Stack my={24} gap={40}>
          <Flex gap={32} align="center">
            <Select label="Select declarant" data={declarantsOptions} value={selectedDeclarant?.value} onChange={changesSelectedDeclarant} />
            {selectedSystem?.hasLRNSearch && (
              <>
                <TextInput
                  label="LRN"
                  value={selectedLRN}
                  onChange={(event) => {
                    setShowNotificationsList(false);
                    setSelectedLRN(event.target.value);
                  }}
                  disabled={!!selectedCorrelationID?.length || !!selectedMRN?.length}
                />
                <Text h="fit-content" c="blue" fw={800} size="xl">
                  or
                </Text>
              </>
            )}
            <TextInput
              label="MRN"
              value={selectedMRN}
              onChange={(event) => {
                setShowNotificationsList(false);
                setSelectedMRN(event.target.value);
                setSelectedTransitGroup(selectedSystem.value === 'ncts' ? transitGroupOptions[0].value : '');
              }}
              disabled={!!selectedCorrelationID?.length || !!selectedLRN?.length}
            />
            {selectedSystem?.value === 'ncts' && !!selectedMRN?.length && (
              <Select
                value={selectedTransitGroup}
                onChange={(value) => {
                  setShowNotificationsList(false);
                  setSelectedTransitGroup(value);
                }}
                label="Select transit group"
                data={transitGroupOptions}
                searchable
                size="lg"
              />
            )}
            <Text h="fit-content" c="blue" fw={800} size="xl">
              or
            </Text>
            <TextInput
              label="Correlation id"
              value={selectedCorrelationID}
              onChange={(event) => {
                setShowNotificationsList(false);
                setSelectedCorrelationID(event.target.value);
              }}
              disabled={!!selectedLRN?.length || !!selectedMRN?.length}
            />
            <Button type="button" onClick={handleSearch} variant="filled" size="lg" disabled={!selectedCorrelationID && !selectedLRN && !selectedMRN}>
              Search
            </Button>
            <Button
              type="button"
              onClick={resetNotificationSearch}
              variant="outline"
              size="lg"
              disabled={!selectedCorrelationID && !selectedLRN && !selectedMRN}
            >
              Clear filters
            </Button>
          </Flex>
          {showNotificationsList && (
            <NotificationsList
              selectedSystem={selectedSystem}
              selectedDeclarant={selectedDeclarant}
              selectedLRN={selectedLRN}
              selectedMRN={selectedMRN}
              selectedCorrelationID={selectedCorrelationID}
              selectedTransitGroup={selectedTransitGroup}
            ></NotificationsList>
          )}
        </Stack>
      </ContentLayout.Content>
    </ContentLayout>
  );
};
